import React from "react";
import { FiX } from "react-icons/fi";
import Select from "../ReusableComponents/Select";
import {
  FREQ_TYPES,
  WEEK_DAYS,
  HOURS,
  MINUTES,
  computeNextRunFromCron,
} from "../../utils/scheduleFrequency";
import {
  ScheduleConfig,
  ScheduleMode,
  scheduleConfigToRunSchedule,
  toggleWeekDay,
} from "./scheduleConfig";

interface ScheduleEditorProps {
  value: ScheduleConfig;
  onChange: (next: ScheduleConfig) => void;
  onRemove?: () => void;
  index?: number;
  /** Which modes to offer; defaults to all. The value's current mode is always shown so an existing schedule can't land on a hidden tab. */
  modes?: ScheduleMode[];
  /** What the preview calls the next fire time (default "Next run"; an expected delivery says "Next issue") */
  nextLabel?: string;
}

// Chrome's list leaves out "UTC" itself (only regional zones), yet UTC is the default for API schedules and
// expected deliveries; without it the picker shows blank for a UTC value
const SUPPORTED_TIMEZONES: string[] = (Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf("timeZone") : [];
const ALL_TIMEZONES: string[] = SUPPORTED_TIMEZONES.includes("UTC") ? SUPPORTED_TIMEZONES : ["UTC", ...SUPPORTED_TIMEZONES];

const selectClass =
  "appearance-none rounded-md border border-[#eceff3] bg-white pl-2 pr-7 py-1.5 text-sm text-[#666d80] focus:outline-none focus:border-gray-300";

const MODES: { key: ScheduleMode; label: string }[] = [
  { key: "recurring", label: "Recurring" },
  { key: "once", label: "Run Once" },
  { key: "custom", label: "Custom cron" },
];

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ value, onChange, onRemove, index, modes, nextLabel = "Next run" }) => {
  const set = (patch: Partial<ScheduleConfig>) => onChange({ ...value, ...patch });
  const visibleModes = modes ? MODES.filter((m) => modes.includes(m.key) || m.key === value.mode) : MODES;

  // Live "next run" preview (best-effort; computeNextRunFromCron has its own fallback).
  let preview = "";
  try {
    const rs = scheduleConfigToRunSchedule(value);
    if (value.mode === "once") {
      preview = value.firstRunTime ? `Runs once at ${value.firstRunTime}` : "Pick a first-run time";
    } else if (rs.cronExpression) {
      preview = `${nextLabel}: ${computeNextRunFromCron(rs.cronExpression, value.timezone)}`;
    }
  } catch {
    preview = "";
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600">
          Schedule {typeof index === "number" ? index + 1 : ""}
        </span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-0.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
            title="Remove schedule"
          >
            <FiX size={16} />
          </button>
        )}
      </div>

      {/* Mode selector */}
      <div className="flex gap-2 flex-wrap">
        {visibleModes.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => set({ mode: m.key })}
            className={`px-3 py-1 text-xs rounded-md border transition-colors ${
              value.mode === m.key
                ? "bg-[#f6f9eb] border-eai-primary-accent text-eai-primary-accent font-medium"
                : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {value.mode === "recurring" && (
        <>
          {/* Frequency chips */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-500">Frequency</label>
            <div className="flex gap-2 flex-wrap">
              {FREQ_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => set({ freqType: type })}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    value.freqType === type
                      ? "bg-[#f6f9eb] border-eai-primary-accent text-eai-primary-accent font-medium"
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {value.freqType === "Weekly" && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-500">Days</label>
              <div className="flex gap-2 flex-wrap">
                {WEEK_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={value.weekDays.includes(day)}
                    onClick={() => set({ weekDays: toggleWeekDay(value.weekDays, day) })}
                    className={`w-10 py-1 text-xs rounded-md border transition-colors ${
                      value.weekDays.includes(day)
                        ? "bg-[#f6f9eb] border-eai-primary-accent text-eai-primary-accent font-medium"
                        : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {value.freqType === "Monthly" && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-500">Day of month</label>
              <input
                type="number"
                min={1}
                max={31}
                value={value.monthDay}
                onChange={(e) =>
                  set({ monthDay: Math.min(31, Math.max(1, Number(e.target.value))) })
                }
                className={`w-20 ${selectClass}`}
              />
            </div>
          )}

          {/* Time picker */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-500">Time</label>
            <div className="flex items-center gap-2">
              <Select
                value={value.hour}
                onChange={(e) => set({ hour: e.target.value })}
                className="w-auto"
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </Select>
              <span className="text-gray-500 text-sm">:</span>
              <Select
                value={value.minute}
                onChange={(e) => set({ minute: e.target.value })}
                className="w-auto"
              >
                {MINUTES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
              <div className="flex rounded-md border border-[#eceff3] overflow-hidden">
                {(["AM", "PM"] as const).map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => set({ ampm: period })}
                    className={`px-3 py-1.5 text-xs transition-colors ${
                      value.ampm === period
                        ? "bg-[#f6f9eb] text-eai-primary-accent font-medium"
                        : "bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {value.mode === "once" && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-500">First run time</label>
          <input
            type="datetime-local"
            value={value.firstRunTime}
            onChange={(e) => set({ firstRunTime: e.target.value })}
            className={`${selectClass} w-full pr-2`}
          />
        </div>
      )}

      {value.mode === "custom" && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-500">Cron expression</label>
          <input
            type="text"
            value={value.cronExpression}
            onChange={(e) => set({ cronExpression: e.target.value })}
            placeholder="0 9 * * ? *"
            className="rounded-md border border-[#eceff3] bg-white px-3 py-2 text-sm text-[#666d80] placeholder:text-gray-400 focus:outline-none focus:border-gray-300"
          />
          <span className="text-xs text-gray-400">
            AWS cron: minute hour day-of-month month day-of-week year
          </span>
        </div>
      )}

      {/* Timezone (shared by all modes) */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-500">Timezone</label>
        <Select
          value={value.timezone}
          onChange={(e) => set({ timezone: e.target.value })}
        >
          {(value.timezone && !ALL_TIMEZONES.includes(value.timezone) ? [value.timezone, ...ALL_TIMEZONES] : ALL_TIMEZONES).map((tz) => (
            <option key={tz} value={tz}>
              {tz.replace(/_/g, " ")}
            </option>
          ))}
        </Select>
      </div>

      {preview && <span className="text-xs text-gray-400">{preview}</span>}
    </div>
  );
};

export default ScheduleEditor;
